import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Globe,
  ExternalLink,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Lightbulb,
  Award,
  ShieldCheck,
  Sparkles,
  ChevronDown,
  Pencil,
  X,
  Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMPACT_STYLES = {
  high:   { bg: "bg-rose-500/15",    ring: "ring-rose-400/30",    text: "text-rose-300",    label: "High Impact" },
  medium: { bg: "bg-amber-500/15",   ring: "ring-amber-400/30",   text: "text-amber-300",   label: "Medium Impact" },
  low:    { bg: "bg-slate-500/15",   ring: "ring-slate-400/30",   text: "text-slate-300",   label: "Nice to Have" },
};

export const LandingPageAnalysis = ({ clientId, clientName, initialUrl = "" }) => {
  const [url, setUrl] = useState(initialUrl);
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState(initialUrl);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchedInitial, setFetchedInitial] = useState(false);

  // Load cached analysis on mount
  useEffect(() => {
    if (!clientId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/clients/${clientId}/landing-page-analysis`);
        if (r.data?.url) setUrl(r.data.url);
        if (r.data?.has_analysis) setData(r.data);
      } catch (e) {
        // non-fatal
      } finally {
        setFetchedInitial(true);
      }
    })();
  }, [clientId]);

  useEffect(() => {
    setUrlDraft(url);
  }, [url]);

  const runAnalysis = async (forceRefresh = false, overrideUrl = null) => {
    const target = (overrideUrl ?? url ?? "").trim();
    if (!target) {
      toast.error("Add a landing page URL first");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/clients/${clientId}/analyze-landing-page`, {
        client_id: clientId,
        url: target,
        force_refresh: forceRefresh,
      });
      setData(r.data);
      setUrl(r.data.url || target);
      toast.success(r.data.cached ? "Loaded cached analysis" : "Landing page analyzed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const saveUrl = async () => {
    const clean = (urlDraft || "").trim();
    if (!clean) return;
    setEditingUrl(false);
    await runAnalysis(true, clean);
  };

  const score = data?.overall_score ?? null;
  const scoreColor = useMemo(() => {
    if (score === null) return "text-slate-400";
    if (score >= 80) return "text-emerald-300";
    if (score >= 60) return "text-amber-300";
    return "text-rose-300";
  }, [score]);

  const scoreRing = useMemo(() => {
    if (score === null) return "ring-slate-500/30";
    if (score >= 80) return "ring-emerald-400/40";
    if (score >= 60) return "ring-amber-400/40";
    return "ring-rose-400/40";
  }, [score]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative z-10 mb-8"
      data-testid="landing-page-analysis"
    >
      <div className="relative overflow-hidden rounded-3xl border border-indigo-400/25 bg-slate-950/85">
        <div className="absolute top-0 right-0 w-[420px] h-[280px] bg-[radial-gradient(ellipse_at_top_right,rgba(99,102,241,0.16),transparent_70%)] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[320px] h-[220px] bg-[radial-gradient(ellipse_at_bottom_left,rgba(236,72,153,0.08),transparent_70%)] pointer-events-none" />

        <div className="relative p-6 md:p-8">
          {/* ====== HEADER / STORY PROMPT ====== */}
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-indigo-300" />
            <p className="text-indigo-300 text-[11px] font-bold uppercase tracking-[0.25em]">
              After the Click · Website Conversion Analysis
            </p>
          </div>

          <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight mb-2">
            Your ads are working.{" "}
            <span className="bg-gradient-to-r from-indigo-300 via-sky-300 to-emerald-300 bg-clip-text text-transparent">
              Is your website?
            </span>
          </h2>
          <p className="text-slate-300 text-sm md:text-base mb-6 max-w-3xl">
            High click-through rates mean nothing if visitors leave before they buy.
            Here's what {clientName}'s landing page is telling customers, and what it's missing.
          </p>

          {/* ====== URL + ACTIONS BAR ====== */}
          <div className="bg-slate-900/70 rounded-2xl border border-white/10 p-4 mb-6 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[220px]">
              <p className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">Landing Page</p>
              {editingUrl ? (
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    type="url"
                    value={urlDraft}
                    onChange={(e) => setUrlDraft(e.target.value)}
                    placeholder="https://www.example.com"
                    data-testid="landing-page-url-input"
                    className="flex-1 bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/50"
                  />
                  <button onClick={saveUrl} data-testid="landing-page-url-save" className="p-2 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-lg text-emerald-300 transition">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => { setEditingUrl(false); setUrlDraft(url); }} className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-slate-400 transition">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {url ? (
                    <a href={url} target="_blank" rel="noreferrer" className="text-white font-semibold hover:text-indigo-300 transition inline-flex items-center gap-1.5 truncate" data-testid="landing-page-url-link">
                      {url}
                      <ExternalLink className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    </a>
                  ) : (
                    <span className="text-slate-500 italic text-sm">No landing page set</span>
                  )}
                  <button onClick={() => setEditingUrl(true)} data-testid="landing-page-url-edit" className="p-1.5 hover:bg-white/10 rounded text-slate-400 hover:text-white transition ml-auto">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => runAnalysis(true)}
              disabled={loading || !url}
              data-testid="landing-page-analyze-btn"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-sky-500 hover:from-indigo-400 hover:to-sky-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 rounded-full text-sm shadow-lg shadow-indigo-500/20 transition"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Analyzing…" : data ? "Re-analyze" : "Analyze Landing Page"}
            </button>
          </div>

          {/* ====== EMPTY STATE ====== */}
          {!loading && !data && fetchedInitial && (
            <div className="bg-slate-900/60 rounded-2xl border border-dashed border-white/15 p-8 text-center">
              <Sparkles className="w-8 h-8 text-indigo-300 mx-auto mb-3" />
              <p className="text-white font-semibold mb-1">Ready when you are.</p>
              <p className="text-slate-300 text-sm max-w-md mx-auto">
                Click <span className="text-indigo-300 font-semibold">Analyze Landing Page</span> to
                get an AI-powered conversion review of {url || "your landing page"}.
              </p>
            </div>
          )}

          {/* ====== LOADING STATE ====== */}
          {loading && !data && (
            <div className="bg-slate-900/60 rounded-2xl border border-white/10 p-10 text-center">
              <RefreshCw className="w-8 h-8 text-indigo-300 mx-auto mb-3 animate-spin" />
              <p className="text-white font-semibold mb-1">Reading the page like a new customer…</p>
              <p className="text-slate-300 text-sm">Takes about 15 seconds.</p>
            </div>
          )}

          {/* ====== ANALYSIS RESULT ====== */}
          {data && data.overall_score !== undefined && (
            <div className="space-y-5">
              {/* Score + verdict */}
              <div className="flex flex-col md:flex-row items-start gap-5 bg-slate-900/70 rounded-2xl border border-white/10 p-5">
                <div className={`relative flex-shrink-0 w-28 h-28 rounded-full ring-2 ${scoreRing} flex items-center justify-center bg-slate-950`}>
                  <div className="text-center">
                    <p className={`text-4xl font-black ${scoreColor} tabular-nums`}>{data.overall_score}</p>
                    <p className="text-slate-500 text-[11px] uppercase tracking-wider">out of 100</p>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-indigo-300 text-[11px] font-bold uppercase tracking-wider mb-1.5">The Verdict</p>
                  <p className="text-white text-lg font-semibold leading-snug mb-3">{data.headline_verdict}</p>
                  {data.first_impression && (
                    <div className="bg-indigo-500/15 border border-indigo-400/25 rounded-xl p-3.5">
                      <p className="text-indigo-200 text-[11px] font-bold uppercase tracking-wider mb-1">5-Second First Impression</p>
                      <p className="text-slate-100 text-sm leading-relaxed">{data.first_impression}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Strengths + Opportunities split */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Strengths */}
                <div className="bg-slate-900/70 border border-emerald-400/30 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Award className="w-4 h-4 text-emerald-300" />
                    <p className="text-emerald-300 text-[11px] font-bold uppercase tracking-wider">What's Working</p>
                  </div>
                  <ul className="space-y-3">
                    {(data.strengths || []).map((s, i) => (
                      <li key={i} className="flex items-start gap-2.5" data-testid={`lp-strength-${i}`}>
                        <CheckCircle2 className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-white text-sm font-semibold leading-tight">{s.title}</p>
                          <p className="text-slate-300 text-xs mt-0.5 leading-relaxed">{s.detail}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Opportunities */}
                <div className="bg-slate-900/70 border border-rose-400/30 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb className="w-4 h-4 text-rose-300" />
                    <p className="text-rose-300 text-[11px] font-bold uppercase tracking-wider">Where to Improve</p>
                  </div>
                  <ul className="space-y-3">
                    {(data.opportunities || []).map((o, i) => {
                      const imp = IMPACT_STYLES[o.impact] || IMPACT_STYLES.medium;
                      return (
                        <li key={i} className="flex items-start gap-2.5" data-testid={`lp-opportunity-${i}`}>
                          <AlertCircle className="w-4 h-4 text-rose-300 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-white text-sm font-semibold leading-tight">{o.title}</p>
                              {o.impact && (
                                <span className={`text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ring-1 ${imp.bg} ${imp.ring} ${imp.text}`}>
                                  {imp.label}
                                </span>
                              )}
                            </div>
                            <p className="text-slate-300 text-xs mt-0.5 leading-relaxed">{o.detail}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* CTA analysis + Trust signals row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {data.cta_analysis && (
                  <div className="bg-slate-900/70 border border-white/10 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-sky-500/20 flex items-center justify-center">
                        <ExternalLink className="w-4 h-4 text-sky-300" />
                      </div>
                      <p className="text-sky-300 text-[11px] font-bold uppercase tracking-wider">Your Primary CTA</p>
                    </div>
                    {data.cta_analysis.primary_cta_found ? (
                      <p className="text-white text-xl font-bold mb-2">"{data.cta_analysis.primary_cta_found}"</p>
                    ) : (
                      <p className="text-slate-400 italic mb-2">No primary CTA detected</p>
                    )}
                    {data.cta_analysis.clarity && (
                      <p className="text-slate-200 text-sm mb-2 leading-relaxed">{data.cta_analysis.clarity}</p>
                    )}
                    {data.cta_analysis.recommendation && (
                      <p className="text-sky-200 text-xs leading-relaxed border-t border-white/10 pt-2 mt-2">
                        <span className="font-semibold">Tip: </span>{data.cta_analysis.recommendation}
                      </p>
                    )}
                  </div>
                )}
                {data.trust_signals && (
                  <div className="bg-slate-900/70 border border-white/10 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <ShieldCheck className="w-4 h-4 text-emerald-300" />
                      </div>
                      <p className="text-emerald-300 text-[11px] font-bold uppercase tracking-wider">Trust Signals</p>
                    </div>
                    {data.trust_signals.present?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-slate-400 text-[11px] uppercase tracking-wider mb-1.5">On Your Page</p>
                        <div className="flex flex-wrap gap-1.5">
                          {data.trust_signals.present.map((t, i) => (
                            <span key={i} className="text-[11px] bg-emerald-500/20 text-emerald-200 px-2 py-0.5 rounded-full border border-emerald-400/30">
                              ✓ {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {data.trust_signals.missing?.length > 0 && (
                      <div>
                        <p className="text-slate-400 text-[11px] uppercase tracking-wider mb-1.5">Consider Adding</p>
                        <div className="flex flex-wrap gap-1.5">
                          {data.trust_signals.missing.map((t, i) => (
                            <span key={i} className="text-[11px] bg-slate-700/60 text-slate-200 px-2 py-0.5 rounded-full border border-slate-500/30">
                              + {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Next 3 moves */}
              {data.next_three_moves?.length > 0 && (
                <div className="bg-slate-900/70 border border-emerald-400/35 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-emerald-300" />
                    <p className="text-emerald-300 text-[11px] font-bold uppercase tracking-wider">Your Next 3 Moves</p>
                  </div>
                  <ol className="space-y-2.5">
                    {data.next_three_moves.map((m, i) => (
                      <li key={i} className="flex items-start gap-3" data-testid={`lp-next-move-${i}`}>
                        <span className="w-6 h-6 rounded-full bg-emerald-500/25 flex items-center justify-center text-emerald-200 text-xs font-bold flex-shrink-0">
                          {i + 1}
                        </span>
                        <p className="text-white text-sm leading-snug pt-0.5">{m}</p>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Meta */}
              {data.analyzed_at && (
                <p className="text-slate-600 text-[11px] text-right">
                  Analyzed {new Date(data.analyzed_at).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
